import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Container,
  Title,
  Stack,
  Text,
  Button,
  Group,
  Card,
  Grid,
  Center,
  Alert,
  Divider
} from '@mantine/core';
import {
  IconShoppingBag,
  IconArrowRight,
  IconTrash,
  IconExclamationCircle,
  IconShoppingCart
} from '@tabler/icons-react';
import { 
  selectCartItems, 
  selectCartTotal, 
  selectCartItemCount, 
  clearCart 
} from '../store/slices/cartSlice';
import CartItem from '../components/cart/CartItem';

const CartPage = () => {
  const dispatch = useDispatch();
  const cartItems = useSelector(selectCartItems);
  const cartTotal = useSelector(selectCartTotal);
  const itemCount = useSelector(selectCartItemCount);

  const handleClearCart = () => {
    if (window.confirm('Are you sure you want to clear your cart?')) {
      dispatch(clearCart());
    }
  };

  const handleCheckout = () => {
    // TODO: Implement checkout functionality
    console.log('Proceeding to checkout with items:', cartItems);
  };

  // Check for any items that are out of stock or unavailable
  const hasUnavailableItems = cartItems.some(item => 
    !item.is_available || item.quantity > item.stock_quantity
  );

  const unavailableItems = cartItems.filter(item => 
    !item.is_available || item.quantity > item.stock_quantity
  );

  if (cartItems.length === 0) {
    return (
      <Container size="lg" py="xl">
        <Center style={{ minHeight: '60vh' }}>
          <Stack gap="xl" align="center" maw={400}>
            <IconShoppingCart size="5rem" color="var(--mantine-color-gray-5)" />
            <Stack gap="md" align="center">
              <Title order={2} ta="center">Your cart is empty</Title>
              <Text c="dimmed" ta="center" size="lg">
                Looks like you haven't added any items to your cart yet. 
                Start shopping to see your items here!
              </Text>
            </Stack>
            <Group gap="md">
              <Button 
                component={Link} 
                to="/products" 
                size="lg"
                leftSection={<IconShoppingBag size="1.2rem" />}
              >
                Browse Products
              </Button>
              <Button 
                component={Link} 
                to="/princesses" 
                variant="outline"
                size="lg"
              >
                Find Princesses
              </Button>
            </Group>
          </Stack>
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        {/* Page Header */}
        <Group justify="space-between" align="center">
          <div>
            <Title order={1}>Shopping Cart</Title>
            <Text c="dimmed" mt="xs">
              {itemCount} {itemCount === 1 ? 'item' : 'items'} in your cart
            </Text>
          </div>
          <Button
            variant="outline"
            color="red"
            leftSection={<IconTrash size="1rem" />}
            onClick={handleClearCart}
          >
            Clear Cart
          </Button>
        </Group>

        {/* Unavailable Items Alert */}
        {hasUnavailableItems && (
          <Alert 
            icon={<IconExclamationCircle size="1rem" />} 
            color="orange"
            title="Some items need attention"
          >
            <Text size="sm">
              {unavailableItems.length} {unavailableItems.length === 1 ? 'item is' : 'items are'} no longer available or out of stock. 
              Please review your cart before proceeding to checkout.
            </Text>
          </Alert>
        )}

        <Grid>
          {/* Cart Items */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="md">
              {cartItems.map((item) => (
                <CartItem key={item.id} item={item} compact={false} />
              ))}
            </Stack>
          </Grid.Col>

          {/* Cart Summary */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder sticky style={{ top: 20 }}>
              <Stack gap="md">
                <Title order={3}>Order Summary</Title>
                <Divider />
                
                <Group justify="space-between">
                  <Text>Subtotal ({itemCount} items):</Text>
                  <Text fw={500}>${cartTotal}</Text>
                </Group>
                
                <Group justify="space-between">
                  <Text>Shipping:</Text>
                  <Text c="dimmed" size="sm">Calculated at checkout</Text>
                </Group>
                
                <Group justify="space-between">
                  <Text>Tax:</Text>
                  <Text c="dimmed" size="sm">Calculated at checkout</Text>
                </Group>
                
                <Divider />
                
                <Group justify="space-between">
                  <Text size="lg" fw={600}>Total:</Text>
                  <Text size="xl" fw={700} c="pink">${cartTotal}</Text>
                </Group>
                
                <Stack gap="sm" mt="md">
                  <Button
                    size="lg"
                    fullWidth
                    onClick={handleCheckout}
                    rightSection={<IconArrowRight size="1rem" />}
                    disabled={hasUnavailableItems}
                  >
                    Proceed to Checkout
                  </Button>
                  
                  <Button
                    variant="outline"
                    fullWidth
                    component={Link}
                    to="/products"
                  >
                    Continue Shopping
                  </Button>
                </Stack>

                {hasUnavailableItems && (
                  <Text size="xs" c="red" ta="center" mt="xs">
                    Please resolve unavailable items before checkout
                  </Text>
                )}

                <Divider mt="md" />
                
                <Stack gap="xs" mt="md">
                  <Text size="sm" fw={500}>Secure Checkout</Text>
                  <Text size="xs" c="dimmed">
                    Your payment information is encrypted and secure
                  </Text>
                  <Text size="xs" c="dimmed">
                    ✓ SSL encrypted checkout
                  </Text>
                  <Text size="xs" c="dimmed">
                    ✓ Princess verified sellers
                  </Text>
                  <Text size="xs" c="dimmed">
                    ✓ Buyer protection guarantee
                  </Text>
                </Stack>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Additional Actions */}
        <Card shadow="sm" padding="md" radius="md" withBorder>
          <Group justify="space-between" align="center">
            <div>
              <Text fw={500}>Need help with your order?</Text>
              <Text size="sm" c="dimmed">Contact our customer support team</Text>
            </div>
            <Button variant="outline">
              Contact Support
            </Button>
          </Group>
        </Card>
      </Stack>
    </Container>
  );
};

export default CartPage;